import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { useElementSize } from '@/utils/use-element-size';
import Image from 'next/image';
import React, { PropsWithChildren } from 'react';
import { Themes } from '../microstory/theme';

type WorldMapProps = ComposableStyledComponentProps<HTMLDivElement> &
  PropsWithChildren<{
    // offset from the center (-1 to 1)
    offset?: number;
    theme?: Themes;
  }>;

const MapRoot = styled('div', {
  maxWidth: '100vw',
  height: '100%',
  position: 'relative',
});

const MapTiles = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 0,
});

const Tile = styled('div', {
  position: 'absolute',
  transformOrigin: 'center',
});

const TileLayer = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

const Box = styled('div', {});

const rnd = (n: number) => Math.round(n);

export function WorldMap(props: WorldMapProps) {
  const { children, css, offset = 0, theme } = props;

  const [root, { width, height }] = useElementSize<HTMLDivElement>();

  let tileSize = width > height ? height : width;
  tileSize = tileSize === 0 ? 480 : tileSize;

  const nTiles = (dimension: number, tileSize: number) => {
    if (Number.isNaN(dimension) || Number.isNaN(tileSize)) return 1;
    const n = Math.ceil(dimension / tileSize);
    return n % 2 === 0 ? n + 1 : n;
  };

  const tilesCoords = (n: number) => {
    if (Number.isNaN(n) || n === 1) return [0];
    const min = -Math.floor(n / 2);
    return Array(n)
      .fill(0)
      .map((_, i) => min + i);
  };

  // Calculate the horizontal and vertical tiles needed, making sure they are always uneven numbers.
  const hTiles = tilesCoords(nTiles(width, tileSize));
  const vTiles = tilesCoords(nTiles(height + height * Math.abs(offset), tileSize));
  const tiles = hTiles.map((x) => vTiles.map((y) => [x, y])).flat();

  return (
    <MapRoot ref={root} css={css}>
      <MapTiles>
        <Box css={{ transform: `translateY(${height * offset}px)` }}>
          {tiles.map(([x, y]) => (
            <Tile
              key={`tile-${x}-${y}`}
              css={{
                top: (height ?? 480) / 2,
                left: (width ?? 480) / 2,
                width: tileSize,
                height: tileSize,
                transform: `translate(-50%, -50%)
                  translate(${rnd(x * tileSize)}px, ${rnd(y * tileSize)}px)
                  rotate(${(x % 2 === 0) === (y % 2 === 0) ? 0 : '180deg'})
                `,
              }}
            >
              <TileLayer css={{ zIndex: 0 }}>
                <Image
                  src={`/api/world-map.svg${theme ? `?theme=${theme}` : ''}`}
                  alt=""
                  unoptimized
                  width={tileSize ?? 480}
                  height={tileSize ?? 480}
                  priority
                />
              </TileLayer>

              <TileLayer css={{ zIndex: 1 }}>
                {React.Children.map(children, (child) => {
                  if (React.isValidElement<{ isCenterTile: boolean; tileSize: number }>(child)) {
                    return React.cloneElement(child, {
                      isCenterTile: x === 0 && y === 0,
                      tileSize: tileSize,
                    });
                  }
                })}
              </TileLayer>
            </Tile>
          ))}
        </Box>
      </MapTiles>
    </MapRoot>
  );
}
